import { useEffect, useRef, useState } from 'react';
import { useNetworkStatus } from './useNetworkStatus';
import offlineSyncService from '../services/OfflineSyncService';
import { useAlert } from '../contexts/AlertContext';
import { t } from 'i18next';

export const useOfflineSync = () => {
  const { isOnline, wasOffline } = useNetworkStatus();
  const { showAlert } = useAlert();
  const hasSyncedRef = useRef(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {

    // When network comes back online after being offline
    if (isOnline && wasOffline && !hasSyncedRef.current) {
      hasSyncedRef.current = true;

      // Delay sync slightly to ensure network is stable
      const syncTimeout = setTimeout(async () => {
        try {
          // Check if there are any pending observations
          const hasPending = await offlineSyncService.hasPendingObservations();
          if (hasPending) {
            setIsSyncing(true);
            const result = await offlineSyncService.syncPendingObservations();
            setIsSyncing(false);

            if (result.success > 0) {
              showAlert(t("observation.syncSuccess"), 'success');

              // Trigger a custom event to notify components that observations were synced
              window.dispatchEvent(new CustomEvent('observationsSynced', {
                detail: { count: result.success, trigger: 'network_reconnection' }
              }));
            }

            if (result.failed > 0) {
              showAlert(t("observation.syncFailed"), 'warning');
            }
          } else {
          }
        } catch (error) {
          console.error('Error during automatic sync after network reconnection:', error);
          setIsSyncing(false);
          showAlert(t("observation.errorSync"), 'error');
        } finally {
          hasSyncedRef.current = false;
        }
      }, 2000); // 2-second delay

      return () => clearTimeout(syncTimeout);
    }

    // NOTE: The component-load sync path has been intentionally removed.
    // The GlobalNetworkMonitor singleton already polls every 5 seconds and
    // handles syncing pending observations on startup / reconnection.
    // Having a duplicate trigger here caused race conditions across tabs.

  }, [isOnline, wasOffline, showAlert]);

  // Reset sync flag when going offline
  useEffect(() => {
    if (!isOnline) {
      hasSyncedRef.current = false;
    }
  }, [isOnline]);

  return {
    isOnline,
    wasOffline,
    isSyncing,
  };
};